import React from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Pill } from 'ui-components/components/pill';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';

import { ManageItemGrid } from '../../../components/manage-item-grid';
import { useModal } from '../../../hooks/use-modal';
import { TagPropType } from '../../../prop-types/tag';
import { AddTagsModal } from '../add-tags-modal';
import { EditTagModal } from '../edit-tag-modal';
import { useManageTags } from './hooks';
import style from './manage-tags-modal.module.less';
import { ADD } from './constants';
import { messages } from './messages';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { EventSourceStrings, Workflows } from '../../../tracking/tracking-constants';

const ORDER_GRID_HEADER = 'ORDER_GRID_HEADER';

export const ManageTagModal = ({ tags, modalFrameworkMethods }) => {
  const { isOpen: isAddTagsOpen, close: closeAddTags, open: openAddTags } = useModal();
  const { isOpen: isEditTagOpen, close: closeEditTag, open: openEditTag } = useModal();
  const { isLoading, editTag, onEditClicked, onDeleteClicked } = useManageTags({
    openEditTag,
    source: ORDER_GRID_HEADER,
  });
  const trackTagManagementWorkflow = useTrackWorkflow(Workflows.TAG_MANAGEMENT_FLOW);

  const { closeModal } = modalFrameworkMethods;
  const hasNoTags = !tags || tags.length === 0;
  const { formatMessage } = useIntl();
  const manageItemGridColumns = [
    {
      name: formatMessage(messages.name),
      itemRenderer: (item) => <Pill className={style['tag-pill']} color={item.hexColor} label={item.name} theme='tag' truncate />,
    },
  ];

  const onOpen = () => {
    openAddTags();
    trackTagManagementWorkflow(WorkflowSteps.START, {
      modalType: ADD,
      source: EventSourceStrings.ORDER_GRID_HEADER_TAG_DROPDOWN,
    });
  };

  return (
    <>
      <Modal className={style['manage-tag-modal']}>
        <ModalHeader closeModal={closeModal}>
          <FormattedMessage {...messages.manageTagsTitle} />
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          {isLoading && <LoadingIndicator className={style['loading-indicator']} />}
          {!isLoading && hasNoTags && (
            <div className={style['no-tags-message']}>
              <FormattedMessage {...messages.noTagsMessage} />
            </div>
          )}
          {!isLoading && !hasNoTags && (
            <ManageItemGrid columns={manageItemGridColumns} items={tags} onItemDeleted={onDeleteClicked} onItemEdited={onEditClicked} />
          )}
        </ModalBody>
        {!isLoading && (
          <ModalFooter className={style['modal-footer']}>
            <Button onClick={onOpen} theme='link'>
              <FormattedMessage {...messages.addTagsButton} />
            </Button>
            <Button onClick={closeModal} theme='primary'>
              <FormattedMessage {...messages.doneButton} />
            </Button>
          </ModalFooter>
        )}
      </Modal>
      {isAddTagsOpen && <AddTagsModal onClose={closeAddTags} />}
      {isEditTagOpen && <EditTagModal onClose={closeEditTag} tag={editTag} />}
    </>
  );
};

ManageTagModal.propTypes = {
  tags: PropTypes.arrayOf(TagPropType).isRequired,
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
};
